import { Tooltip } from "@/components/TooltipV2";
import { ButtonColor, ButtonSize } from "@/components/interactive/Button";
import { classNames, cx } from "@/utils/classnames";
import { urlIsSameDomain } from "@/utils/link-utils";
import { Placement } from "@floating-ui/react-dom-interactions";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { ComponentProps, forwardRef } from "react";
import { Icon, IconName, IconSize } from "../icons/Icon";

export type IconButtonColor = Exclude<ButtonColor, "accent-lime">;
export const IconButtonColors = [
  "primary",
  "secondary",
  "danger",
  "danger-subdued",
  "warning",
  "white",
] satisfies IconButtonColor[];

export type IconButtonKind = "icon-only" | "outline";
export const IconButtonKinds = ["icon-only", "outline"] satisfies IconButtonKind[];

export const iconKindClasses = {
  "icon-only": "border-0 rounded-full",
  outline: "border rounded",
} satisfies Record<IconButtonKind, string>;

const darkClassName = classNames(
  "dark:text-white dark:outline-white dark:border-white dark:opacity-80",
  "dark:hover:opacity-90 dark:hover:bg-white/10",
  "dark:focus:opacity-90 dark:focus:bg-white/10",
  "dark:active:opacity-100 dark:active:bg-white/20",
);

export const iconColorClasses = {
  primary: classNames(
    "text-cds-bright-green-500 border-cds-bright-green-500 outline-cds-bright-green-600", // base style
    "hover:text-cds-bright-green-600 hover:border-cds-bright-green-600 hover:fill-cds-bright-green-600 hover:bg-cds-bright-green-300/25", // hover effect
    "focus:text-cds-bright-green-600 focus:border-cds-bright-green-600 focus:fill-cds-bright-green-600 focus:bg-cds-bright-green-300/25", // focus (same as hover)
    "active:text-cds-bright-green-600 active:border-cds-bright-green-600 active:bg-cds-bright-green-300/50", // active/pressed effect
    // dark mode
    "dark:!text-cds-bright-green-500 dark:!border-cds-bright-green-500 dark:!outline-cds-bright-green-400", // base style
    "dark:hover:!text-cds-bright-green-600 dark:hover:!border-cds-bright-green-600 dark:hover:!fill-cds-bright-green-600 dark:hover:bg-cds-bright-green-300/25", // hover effect
    "dark:focus:!text-cds-bright-green-600 dark:focus:!border-cds-bright-green-600 dark:focus:!fill-cds-bright-green-600 dark:focus:bg-cds-bright-green-300/25", // focus (same as hover)
    "dark:active:!text-cds-bright-green-600 dark:active:!border-cds-bright-green-600 dark:active:!bg-cds-bright-green-300/50", // active/pressed effect
  ),
  secondary: classNames(
    "text-cds-text-secondary border-cds-gray-300 outline-cds-gray-500", // base style
    "hover:text-cds-gray-500 hover:border-cds-gray-400 hover:fill-cds-gray-500", // hover effect
    "focus:text-cds-gray-500 focus:border-cds-gray-400 focus:fill-cds-gray-500", //focus effect
    "active:text-cds-gray-600 active:border-cds-gray-600 hover:bg-zinc-300/25 active:bg-zinc-300/50", // active/pressed effect,
    darkClassName,
  ),
  danger: classNames(
    "text-red-500 border-red-500 fill-red-500 outline-red-600", // base style
    "hover:text-red-500 hover:border-red-500 hover:fill-red-600 hover:bg-red-300/25", // hover effect
    "hover:text-red-500 hover:border-red-500 focus:fill-red-600 focus:bg-red-300/25", // focus effect
    "active:text-red-600 active:border-red-600 active:bg-red-300/50", // active/pressed effect,
  ),
  // Secondary when inactive, danger when interacting
  "danger-subdued": classNames(
    "text-cds-text-secondary border-cds-gray-300 outline-red-600", // base style
    "hover:text-red-500 hover:border-red-500 hover:fill-red-600 hover:bg-red-300/25", // hover effect
    "focus:text-red-500 focus:border-red-500 focus:fill-red-600 focus:bg-red-300/25", // focus effect
    "active:text-red-600 active:border-red-600 active:bg-red-300/50", // active effect
  ),
  warning: classNames(
    "text-yellow-600 border-yellow-600 fill-yellow-600 outline-yellow-600 ", // base style
    "hover:fill-yellow-700 hover:border-yellow-700 hover:bg-yellow-300/25", // hover effect
    "focus:border-yellow-700 focus:border-yellow-700 focus:border-yellow-700 focus:bg-yellow-300/25", // focus effect
    "active:text-yellow-700 active:border-yellow-700 active:bg-yellow-300/50", // active effect
  ),
  white: classNames(
    "text-white outline-white border-white opacity-80",
    "hover:opacity-90 hover:bg-white/10",
    "focus:opacity-90 focus:bg-white/10",
    "active:opacity-100 active:bg-white/20",
  ),
} satisfies Record<IconButtonColor, string>;

export const iconSizeClasses = {
  xxs: "text-xxs h-[14px] w-[14px] p-0.5",
  xs: "text-xs w-6 h-6 p-1",
  sm: "text-cds-sm w-8 h-8 p-1.5",
  md: "text-base w-10 h-10 p-2",
  lg: "text-cds-lg w-12 h-12 p-2",
  xl: "text-cds-lg w-14 h-14 p-2",
};

export const innerIconSizeClasses = {
  xxs: "xs",
  xs: "sm",
  sm: "md",
  md: "lg",
  lg: "xl",
  xl: "2xl",
} satisfies Record<ButtonSize, IconSize>;

export interface IconButtonProps
  extends Omit<ComponentProps<"button">, "color" | "ref" | "children"> {
  size?: ButtonSize;
  color?: IconButtonColor;
  kind?: IconButtonKind;
  href?: string;
  customColor?: string;
  target?: string;
  rel?: string;
  iconName: IconName;
  iconClassName?: string;
  tooltipText?: string;
  tooltipPlacement?: Placement;
  tooltipInstant?: boolean;
}

/** A button made solely for one icon */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    // children,
    className,
    size = "md",
    color = "secondary",
    kind = "icon-only",
    href,
    customColor,
    rel,
    target,
    iconName,
    iconClassName,
    tooltipText,
    tooltipPlacement,
    tooltipInstant,
    type = "button",
    ...buttonProps
  },
  ref,
) {
  const commonProps = {
    className: classNames(
      "inline-flex items-center justify-center -my-1 align-middle",
      "rounded-full transition-colors duration-75 ease-out hover:ease-in active:ease-out",
      "disabled:pointer-events-none",
      "flex justify-center group/icon-button ",
      iconColorClasses[color],
      iconKindClasses[kind],
      iconSizeClasses[size],
      className,
      buttonProps.disabled && "button-flat-disabled",
    ),
    rel,
    target,
    type,
    style: { color: customColor },
    children:
      iconName != null ? (
        <Icon name={iconName} size={innerIconSizeClasses[size]} className={cx(iconClassName)} />
      ) : null,
  };

  let component;

  if (href != null) {
    component = urlIsSameDomain(href) ? (
      <ChalkLink {...commonProps} unstyled aria-label={buttonProps["aria-label"]} href={href} />
    ) : (
      <a {...commonProps} aria-label={buttonProps["aria-label"]} href={href} />
    );
  } else {
    component = <button {...buttonProps} {...commonProps} ref={ref} />;
  }

  if (tooltipText) {
    component = (
      <Tooltip content={tooltipText} placement={tooltipPlacement} instant={tooltipInstant}>
        {buttonProps.disabled ? <span className="inline-flex">{component}</span> : component}
      </Tooltip>
    );
  }

  return component;
});

export interface DivIconButtonProps extends Omit<ComponentProps<"div">, "color"> {
  size?: ButtonSize;
  color?: IconButtonColor;
  href?: string;
  customColor?: string;
}

/** A button made solely for one icon */
export const DivIconButton = forwardRef<HTMLDivElement, DivIconButtonProps>(function IconButton(
  { children, className, size = "md", color = "secondary", href, customColor, ...buttonProps },
  ref,
) {
  const commonProps = {
    className: classNames(
      "icon-button flex justify-center",
      iconSizeClasses[size],
      iconColorClasses[color],
      className,
    ),
    style: { color: customColor },
    children: typeof children === "string" ? <span className="block">{children}</span> : children,
  };

  if (href != null) {
    return urlIsSameDomain(href) ? (
      <ChalkLink {...commonProps} unstyled href={href} />
    ) : (
      <a {...commonProps} href={href} />
    );
  }

  return <div {...buttonProps} {...commonProps} ref={ref} />;
});
